import { useState } from 'react'
import { TPatient } from 'src/types/patient'
import { useTranslation } from 'react-i18next'
import { CurecallButton, CurecallConfirmationModal, ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { useSession } from 'src/providers/auth.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { PatientService } from 'src/services/patient.service'
import { useNotification } from 'src/providers/notification.provider'
import { useTestAccountPool } from 'src/providers/test-account-pool.provider'
import TestAccountPoolModal from './test-account-pool-modal.component'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'

type Props = {
  patient: TPatient
}

const DemoControls = ({ patient }: Props) => {
  const [isResetModalOpen, setIsResetModalOpen] = useState(false)
  const [isOpenTestAccountModal, setIsOpenTestAccountModal] = useState(false)

  const { project } = useCurrentProject()
  const { patientId } = useParams()

  const queryClient = useQueryClient()
  const { currentUser } = useSession()
  const { notify } = useNotification()
  const { t } = useTranslation()
  const { handleAddTestAccount: addToTestAccountPool, isPatientInTestAccountPool } = useTestAccountPool()

  const isDemo = patient.isTestAccount && currentUser?.isAdminOrSales

  if (!isDemo) return null

  const handleAddTestAccount = () => {
    if (isPatientInTestAccountPool(patient.id)) {
      setIsOpenTestAccountModal(true)
    } else {
      addToTestAccountPool({
        id: patient.id,
        firstname: patient.firstname,
        lastname: patient.lastname,
        phone: patient.phone,
      })
      setIsOpenTestAccountModal(true)
    }
  }

  const resetPatient = async () => {
    try {
      await PatientService.resetPatient(project.id, Number(patientId))
      await queryClient.invalidateQueries({ queryKey: ['patient-timeline', project.id, patientId] })
      setIsResetModalOpen(false)
      notify({
        message: t('pages.patient-page.reset-notification-success'),
        title: t('common.actions.operation-complete'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    } catch (error) {
      notify({
        title: t('components.demo-group.reset-patient-error-title'),
        message: error,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  return (
    <div>
      {isResetModalOpen && (
        <CurecallConfirmationModal
          color="ALERT"
          title={t('components.demo-group.reinitialise')}
          isOpen={isResetModalOpen}
          cancelButtonLabel={t('common.actions.cancel')}
          confirmButtonLabel={t('common.actions.confirm')}
          onCancel={() => setIsResetModalOpen(false)}
          onConfirm={resetPatient}
        >
          {t('components.demo-group.are-you-sure-to-reinitialise')}
        </CurecallConfirmationModal>
      )}

      {isOpenTestAccountModal && (
        <TestAccountPoolModal
          isOpen={isOpenTestAccountModal}
          onClose={() => setIsOpenTestAccountModal(false)}
        />
      )}

      <div className="c-flex c-gap-2">
        <CurecallButton onClick={() => setIsResetModalOpen(true)}>
          <span className="c-flex c-items-center c-gap-1">
            <AdminOnlyBadge variant="button" />
            {t('components.demo-group.reinitialise')}
          </span>
        </CurecallButton>
        <CurecallButton onClick={handleAddTestAccount}>
          <span className="c-flex c-items-center c-gap-1">
            <AdminOnlyBadge variant="button" />
            {isPatientInTestAccountPool(patient.id)
              ? t('components.demo-group.update-demo-group')
              : t('components.demo-group.add-to-demo-group')}
          </span>
        </CurecallButton>
      </div>
    </div>
  )
}

export default DemoControls
